import { he } from '../../../locales/he'
import { IOpenGraph } from '../../../types'
import { isStoriesBlock, IStory, IStoryCard } from '../../../types/stories'
import { parseStories, storyLabel } from './storiesData'

/**
 * Share link of a clip: the page that holds the block plus ?shorts=<part id>.
 * The episode pages of the shows redirect to the OTT site, so the link stays on
 * the page the viewer was opened from. The server gives that address the
 * episode's preview (pages/shorts-share).
 */
export const SHORTS_LINK_PARAM = 'shorts'

const PARAM_PATTERN = /^\d+$/

/** a part without an id shares the page itself */
export const buildShortsLink = (
  origin: string,
  pathname: string,
  cardId: string
) =>
  `${origin}${pathname}` +
  (PARAM_PATTERN.test(cardId) ? `?${SHORTS_LINK_PARAM}=${cardId}` : '')

export const parseShortsParam = (value: string | null | undefined) => {
  const cardId = (value ?? '').trim()
  return PARAM_PATTERN.test(cardId) ? cardId : null
}

/** the address without the parameter (kept out of the history once handled) */
export const withoutShortsParam = (href: string) => {
  const url = new URL(href)
  url.searchParams.delete(SHORTS_LINK_PARAM)
  return url.toString()
}

/** a path of the site without the parameter; the rest of it as it was */
export const withoutShortsInPath = (path: string) => {
  const hashAt = path.indexOf('#')
  const hash = hashAt === -1 ? '' : path.slice(hashAt)
  const rest = hashAt === -1 ? path : path.slice(0, hashAt)
  const queryAt = rest.indexOf('?')
  if (queryAt === -1) {
    return path
  }
  const params = new URLSearchParams(rest.slice(queryAt + 1))
  if (!params.has(SHORTS_LINK_PARAM)) {
    return path
  }
  params.delete(SHORTS_LINK_PARAM)
  const query = params.toString()
  return rest.slice(0, queryAt) + (query ? `?${query}` : '') + hash
}

/**
 * Next's own record of the history entry (`url`, `as`) without the parameter
 * too: Back to this entry from a later page would bring it, and the player,
 * back otherwise
 */
export const withoutShortsInState = (state: unknown): unknown => {
  if (typeof state !== 'object' || state === null) {
    return state
  }
  const next: Record<string, unknown> = { ...state }
  if (typeof next.url === 'string') {
    next.url = withoutShortsInPath(next.url)
  }
  if (typeof next.as === 'string') {
    next.as = withoutShortsInPath(next.as)
  }
  return next
}

/** the story of the page's Stories blocks that holds a part, and that part */
export const findSharedPart = (grid: unknown, cardId: string) => {
  const blocks = (Array.isArray(grid) ? grid : []).filter(isStoriesBlock)
  for (const block of blocks) {
    for (const story of parseStories(block)) {
      const card = story.cards.find(candidate => candidate.id === cardId)
      if (card) {
        return { story, card }
      }
    }
  }
  return null
}

/**
 * The page's preview (og) for a shared link: the episode's cover and title.
 * Its size is unknown, so the page's size goes too.
 */
export const sharedPartOg = (
  og: IOpenGraph,
  story: IStory,
  card: IStoryCard
): IOpenGraph => {
  let url = og.url
  let image = story.cover.d
  try {
    const page = new URL(og.url)
    page.searchParams.set(SHORTS_LINK_PARAM, card.id)
    url = page.toString()
    image = new URL(story.cover.d, page.origin).toString()
  } catch {
    // no address of its own: the page's stays
  }
  return {
    ...og,
    title: `${storyLabel(story) || story.title} | ${he.site_name}`,
    description: card.description || og.description,
    url,
    image,
    image_width: '',
    image_height: '',
  }
}

/**
 * The old way to copy, for where the Clipboard API is missing or refused (apps'
 * in-app browsers, old Safari, http). Deprecated, but nothing replaces it here.
 */
export const copyByCommand = (text: string) => {
  // beside the button pressed: the open player's dialog keeps the rest of the
  // page out of reach
  const pressed =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null
  const field = document.createElement('textarea')
  field.value = text
  field.readOnly = true
  field.style.position = 'fixed'
  field.style.top = '0'
  field.style.opacity = '0'
  ;(pressed?.parentElement ?? document.body).appendChild(field)
  try {
    field.select()
    field.setSelectionRange(0, text.length)
    return document.execCommand('copy')
  } catch {
    return false
  } finally {
    field.remove()
    pressed?.focus({ preventScroll: true })
  }
}
